import {useState, type ReactNode} from 'react';
import {Loader2, AlertTriangle} from 'lucide-react';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@common-web/components/ui/dialog';
import type {GroupConfig} from '@common/enrollmentSite/types';

type SiteRow = RouterOutput['internal']['enrollmentSite']['list'][number];

const STATUS_LABEL = {DRAFT: 'Draft', LIVE: 'Live', LIVE_EDITS: 'Live · edits'} as const;

// Modal to copy another site's render content into the current builder draft.
// Content only — the caller keeps the target's identity and resets e123. Copying
// overwrites the draft, so when there are unsaved edits a copy button flips to an
// inline confirm (no stacked modal) before it replaces them.
export function TemplatePickerDialog({
  currentSlug,
  dirty,
  onApply,
  onClose,
}: {
  currentSlug: string;
  dirty: boolean;
  onApply: (config: GroupConfig, sourceName: string) => void;
  onClose: () => void;
}) {
  const {data: sites, isLoading} = trpc.internal.enrollmentSite.list.useQuery();
  const utils = trpc.useUtils();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{slug: string; version: 'published' | 'draft'} | null>(null);

  const options = (sites ?? []).filter((s) => s.groupSlug !== currentSlug);

  // Unsaved edits → confirm the overwrite in place; otherwise copy straight away.
  function pick(site: SiteRow, version: 'published' | 'draft') {
    if (dirty) {
      setConfirming({slug: site.groupSlug, version});
      return;
    }
    void copy(site, version);
  }

  async function copy(site: SiteRow, version: 'published' | 'draft') {
    setBusy(`${site.groupSlug}:${version}`);
    try {
      const d = await utils.internal.enrollmentSite.get.fetch({groupSlug: site.groupSlug});
      const cfg =
        version === 'published' && d.publishedSiteConfig ? d.publishedSiteConfig : d.siteConfig;
      onApply(cfg as GroupConfig, site.name);
    } catch {
      setBusy(null);
      toast({title: 'Could not load that site — try again.'});
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Copy from another site</DialogTitle>
          <DialogDescription>Start this site from another site’s content.</DialogDescription>
        </DialogHeader>

        <div className='flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800'>
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
          <p>
            Copying overwrites this site’s current draft, <strong>including any unsaved changes</strong>.
            After you copy, review it, then Save to keep the changes, or Discard to revert to the last
            saved copy. Submission mappings are not copied, so set them in the Submission mapping step.
          </p>
        </div>

        {isLoading ? (
          <div className='flex justify-center py-10'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
          </div>
        ) : options.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            No other sites to copy from.
          </p>
        ) : (
          <div className='max-h-80 space-y-1 overflow-y-auto'>
            {options.map((s) => (
              <div key={s.id} className='flex items-center gap-3 rounded-lg border px-3 py-2'>
                <span
                  className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-semibold text-white'
                  style={{backgroundColor: s.primaryColor || '#1F3A5F'}}
                >
                  {s.name.slice(0, 2).toUpperCase()}
                </span>
                <span className='min-w-0 flex-1'>
                  <span className='block truncate text-sm font-medium'>{s.name}</span>
                  <span className='block text-xs text-muted-foreground'>
                    {s.planCount} plan{s.planCount === 1 ? '' : 's'} · {STATUS_LABEL[s.status]}
                  </span>
                </span>
                <div className='flex shrink-0 items-center gap-1.5'>
                  {confirming?.slug === s.groupSlug ? (
                    <>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-8'
                        onClick={() => setConfirming(null)}
                        disabled={!!busy}
                      >
                        Cancel
                      </Button>
                      <Button
                        size='sm'
                        className='h-8 bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        onClick={() => copy(s, confirming.version)}
                        disabled={!!busy}
                      >
                        {busy === `${s.groupSlug}:${confirming.version}` && (
                          <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                        )}
                        Overwrite draft
                      </Button>
                    </>
                  ) : s.status === 'LIVE_EDITS' ? (
                    <>
                      <CopyBtn busy={busy === `${s.groupSlug}:published`} onClick={() => pick(s, 'published')}>
                        Copy published
                      </CopyBtn>
                      <CopyBtn busy={busy === `${s.groupSlug}:draft`} onClick={() => pick(s, 'draft')}>
                        Copy draft
                      </CopyBtn>
                    </>
                  ) : s.status === 'DRAFT' ? (
                    <CopyBtn busy={busy === `${s.groupSlug}:draft`} onClick={() => pick(s, 'draft')}>
                      Copy draft
                    </CopyBtn>
                  ) : (
                    <CopyBtn busy={busy === `${s.groupSlug}:published`} onClick={() => pick(s, 'published')}>
                      Copy
                    </CopyBtn>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CopyBtn({
  busy,
  onClick,
  children,
}: {
  busy: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button variant='outline' size='sm' className='h-8' onClick={onClick} disabled={busy}>
      {busy && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
      {children}
    </Button>
  );
}
